import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiClock, FiCheckCircle, FiSearch, FiTrendingUp } from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import Pagination from '../../components/Pagination/Pagination';
import { useCurrency } from '../../app/CurrencyContext';
import styles from './InrPaymentHistory.module.css';
import axiosClient from '../../api/axiosClient';

interface Payment {
  id: string; amount: string; amount_inr_equivalent: string | null; status: string; transaction_ref: string | null;
  payment_method_type: string | null; created_at: string; currency: string; type: string;
}

const CURRENCY_SYMBOL: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', USDT: '' };
const STATUS_COLORS: Record<string, string> = {
  pending: '#e8a86a', processing: '#1d60f6', paid: '#5cb87d', rejected: '#e2534a',
};
const PAGE_SIZE = 10;

const InrPaymentHistory: React.FC = () => {
  const { convertTransaction, format, currencies: adminCurrencies } = useCurrency();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    axiosClient.get('/funds/mine', {
      params: {
        ...(status !== 'all' ? { status } : {}),
        ...(search ? { search } : {}),
      },
    }).then(({ data }) => setPayments(data.requests.filter((r: Payment) => r.type === 'deposit'))).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); setPage(1); }, [status]);
  useEffect(() => {
    const t = setTimeout(() => { load(); setPage(1); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalInvested = payments.filter((p) => p.status === 'paid').reduce((sum, p) => sum + convertTransaction(Number(p.amount), p.currency, Number(p.amount_inr_equivalent ?? p.amount)), 0);
  const pending = payments.filter((p) => p.status === 'pending').length;
  const approved = payments.filter((p) => p.status === 'paid').length;
  const totalPages = Math.max(1, Math.ceil(payments.length / PAGE_SIZE));
  const pagedPayments = payments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <DashboardShell>
      <div className={styles.page}>
        <Link to="/dashboard/investment" className={styles.backLink}><FiArrowLeft /> Back</Link>
        <h1 className={styles.title}>Payment History</h1>
        <p className={styles.subtitle}>Track your deposit transactions</p>

        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div><span>Total Deposited</span><b>{format(totalInvested)}</b></div>
            <FiDollarSign className={styles.statIcon} />
          </div>
          <div className={styles.statCard}>
            <div><span>Pending</span><b>{pending}</b></div>
            <FiClock className={`${styles.statIcon} ${styles.iconWarn}`} />
          </div>
          <div className={styles.statCard}>
            <div><span>Approved</span><b>{approved}</b></div>
            <FiCheckCircle className={`${styles.statIcon} ${styles.iconSuccess}`} />
          </div>
        </div>

        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <FiSearch />
            <input placeholder="Search by Transaction ID..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="paid">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className={styles.listWrap}>
          {loading && <div className={styles.muted}>Loading…</div>}
          {!loading && payments.length === 0 && (
            <div className={styles.empty}>
              <FiTrendingUp size={30} />
              <p>No payment records found</p>
            </div>
          )}
          {!loading && pagedPayments.map((p) => (
            <div key={p.id} className={styles.row}>
              <div>
                <b>{adminCurrencies.find((c) => c.code === p.currency)?.symbol || CURRENCY_SYMBOL[p.currency] || p.currency + ' '}{Number(p.amount).toLocaleString()}</b>
                <div className={styles.rowSub}>
                  {p.transaction_ref || '—'} · {p.payment_method_type || 'N/A'} · {new Date(p.created_at).toLocaleString()}
                </div>
              </div>
              <span className={styles.statusPill} style={{ color: STATUS_COLORS[p.status], borderColor: STATUS_COLORS[p.status] }}>{p.status}</span>
            </div>
          ))}
        </div>
        {!loading && payments.length > PAGE_SIZE && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </DashboardShell>
  );
};

export default InrPaymentHistory;